import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import UploadZone from "@/components/UploadZone";
import GeneratedLinks from "@/components/GeneratedLinks";
import { Button } from "@/components/ui/button";
import { Zap, ShieldCheck, Code2, Share2, ArrowRight } from "lucide-react";

const features = [
  { icon: Zap, title: "Instant links", desc: "Direct, HTML, BBCode & Markdown in one click." },
  { icon: Share2, title: "Share anywhere", desc: "Short links, QR codes & social buttons built in." },
  { icon: ShieldCheck, title: "Upload anonymously", desc: "No account needed — sign in to keep a history." },
];

export default function Home() {
  const { user, login } = useAuth();
  const [results, setResults] = useState([]);
  const addResult = (img) => setResults((prev) => [img, ...prev]);

  return (
    <div className="dot-grid min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* hero */}
        <div className="max-w-3xl mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card text-xs font-medium text-muted-foreground mb-5">
            <Code2 className="h-3.5 w-3.5 text-accent" /> Embed · Link · Share
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Turn any image into
            <span className="text-accent"> instant embed links.</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground mt-4 max-w-xl">
            Upload a file or paste a URL — ImageLink generates direct links, HTML, BBCode, Markdown,
            short links, QR codes and social share buttons in a single step.
          </p>
        </div>

        {/* uploader */}
        <div className="rounded-3xl border border-border bg-card/80 backdrop-blur-sm p-5 sm:p-8 shadow-sm">
          <UploadZone onCreated={addResult} />
        </div>

        {/* results */}
        {results.length > 0 && (
          <div className="mt-8 space-y-6" data-testid="results-list">
            {results.length > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">
                  {results.length} images ready
                </p>
                <button
                  onClick={() => setResults([])}
                  data-testid="clear-results-btn"
                  className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}
            {results.map((img) => (
              <GeneratedLinks key={img.id} image={img} />
            ))}
            {!user && (
              <div className="flex items-center justify-between flex-wrap gap-3 rounded-xl border border-border bg-secondary/40 px-5 py-4">
                <p className="text-sm text-muted-foreground">
                  Uploaded anonymously. <span className="text-foreground font-medium">Sign in</span> to save these to your history and share them to the gallery.
                </p>
                <Button onClick={login} size="sm" data-testid="home-signin-cta" className="rounded-full">
                  Sign in with Google <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* features */}
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 mt-14">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-6">
              <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-accent" />
              </div>
              <h3 className="font-heading font-semibold text-base">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/gallery" className="text-sm font-medium text-accent hover:underline inline-flex items-center gap-1" data-testid="home-browse-gallery">
            Browse the public gallery <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
